import type { DropdownMenuItem, DropdownMenuProps } from '@timelyapp/react';
import { Box, Button, DropdownMenu, Icon, Text } from '@timelyapp/react';
import classNames from 'classnames';
import { HOUR_STATE_BILLED_AND_LOCKED, HOUR_STATE_LOCKED } from 'constants/filters';
import { concat, without } from 'lodash-es';
import React, { type ReactElement, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHref } from 'react-router-dom';
import {
  getHourStates,
  getUserFilterableHourStates,
  getUserSelectableHourStates,
} from 'redux/modules/hourStates';
import { getAccountFeature } from 'redux/selectors/accounts';
import { getIsUserAdmin } from 'redux/selectors/user';
import type { IState } from 'redux/state';
import { getHourStateIcon } from 'routes/hourStates/HourStateIcon';
import styles from './HourStateDropdown.module.scss';

interface HourStateDropdownProps {
  onChange: (selectedIds: Array<number> | null) => void;
  children: ReactElement;
  disableBillable?: boolean;
  multiselect?: boolean;
  selectedStateIds?: Array<number>;
  disabled?: boolean;
  filterMode?: boolean;
  showNoState?: boolean;
  open?: boolean | undefined;
  onOpenChange?(open: boolean): void;
}

const HourStateDropdown = React.memo<HourStateDropdownProps & Partial<DropdownMenuProps>>(
  ({
    onChange,
    children,
    disableBillable = false,
    multiselect = false,
    selectedStateIds = [],
    disabled = false,
    filterMode = true,
    showNoState = false,
    open,
    onOpenChange,
    ...rest
  }) => {
    const userSelectableStates = useSelector(getUserSelectableHourStates);
    const userFilterableStates = useSelector(getUserFilterableHourStates);
    const isBillingFeatureEnabled = useSelector((state: IState) =>
      getAccountFeature(state, 'billing')
    );
    const isHourStatesFeatureEnabled = useSelector((state: IState) =>
      getAccountFeature(state, 'hour_states')
    );
    const statesById = useSelector(getHourStates);
    const isAdmin = useSelector(getIsUserAdmin);
    const settingsWorkflowPage = useHref('/settings/workflow');
    const settingsSubscriptionPage = useHref('/settings/subscription');
    const visibleStates = useMemo(() => {
      const states = Object.values(filterMode ? userFilterableStates : userSelectableStates).filter(
        s => s.id !== 0
      );
      if (showNoState) {
        states.unshift({
          ...statesById[0],
          name: 'No state',
        });
      }
      return states;
    }, [filterMode, showNoState, statesById, userFilterableStates, userSelectableStates]);

    const menuItems = useMemo(() => {
      const options = visibleStates.map<DropdownMenuItem>(state => {
        return {
          label: state.name,
          disabled: disableBillable && state.billed,
          icon: getHourStateIcon(state),
          ...(multiselect
            ? {
                type: 'checkboxItem',
                checked: selectedStateIds.includes(state.id),
                onCheckedChange: checked =>
                  onChange(
                    checked
                      ? concat(selectedStateIds, state.id)
                      : without(selectedStateIds, state.id)
                  ),
              }
            : {
                type: 'item',
                onSelect: () => onChange([state.id]),
              }),
        };
      });
      const sharedProps = multiselect
        ? ({
            type: 'checkboxItem',
            checked: false,
            onCheckedChange: () => {},
          } as const)
        : ({
            type: 'item',
            onSelect: () => {},
          } as const);
      if (!isHourStatesFeatureEnabled && isBillingFeatureEnabled && isAdmin) {
        options.push({
          label: HOUR_STATE_LOCKED,
          disabled: true,
          icon: <Icon name="Lock01" type="solid" />,
          ...sharedProps,
        });
        options.push({
          label: HOUR_STATE_BILLED_AND_LOCKED,
          disabled: true,
          icon: <Icon name="Lock01" type="solid" />,
          ...sharedProps,
        });
      }
      if (!isBillingFeatureEnabled && isAdmin) {
        options.push({
          label: 'Pending approval',
          disabled: true,
          icon: <Icon name="Eye" type="solid" />,
          ...sharedProps,
        });
        options.push({
          label: 'Verified',
          disabled: true,
          icon: <Icon name="CheckVerified02" type="solid" />,
          ...sharedProps,
        });
        options.push({
          label: 'Billed',
          disabled: true,
          icon: <Icon name="BankNote01" type="solid" />,
          ...sharedProps,
        });
      }
      if (!filterMode || !isHourStatesFeatureEnabled) {
        const helpCenterButton = (
          <Button asChild priority="xlow" size="small">
            <a
              href="https://support.timelyapp.com/en/articles/8770432-time-entry-states"
              rel="noopener"
              target="_blank"
            >
              <Icon type="solid" name="Share04" />
              Help center
            </a>
          </Button>
        );
        let content = null;
        if (isHourStatesFeatureEnabled) {
          content = (
            <Box.Column gap="xlarge">
              <Text.Paragraph size="small">
                Time entry states are custom statuses that streamline workflows, support
                configurable permissions, and are filterable in reports.
              </Text.Paragraph>
              <Box.Row gap="medium">
                {isAdmin && (
                  <Button asChild size="small">
                    <a href={settingsWorkflowPage}>Edit states</a>
                  </Button>
                )}
                {helpCenterButton}
              </Box.Row>
            </Box.Column>
          );
        } else if (isAdmin) {
          content = (
            <Box.Column gap="xlarge">
              <Box.Column gap="medium">
                <Text.Label size="small" color="normal">
                  Upgrade to add custom states
                </Text.Label>
                <Text.Paragraph size="small" color="normal">
                  Time entry states are custom statuses that streamline workflows, support
                  configurable permissions, and are filterable in reports.
                </Text.Paragraph>
              </Box.Column>
              <Box.Row gap="medium">
                <Button asChild size="small" priority="xhigh">
                  <a href={settingsSubscriptionPage}>Unlock Premium</a>
                </Button>
                {helpCenterButton}
              </Box.Row>
            </Box.Column>
          );
        }
        if (content) {
          options.push({ type: 'separator' });
          options.push({
            type: 'message',
            variant: 'info',
            content,
          });
        }
      }
      return options;
    }, [
      disableBillable,
      filterMode,
      isAdmin,
      isBillingFeatureEnabled,
      isHourStatesFeatureEnabled,
      multiselect,
      onChange,
      selectedStateIds,
      settingsSubscriptionPage,
      settingsWorkflowPage,
      visibleStates,
    ]);

    return visibleStates.length > 0 ? (
      <DropdownMenu
        open={open}
        onOpenChange={onOpenChange}
        closeOnSelect
        trigger={children}
        items={menuItems}
        {...rest}
        disabled={disabled}
        style={{ zIndex: 1002 }} // TODO: try to find a better value here
        contentClassName={classNames('ignore-react-onclickoutside', styles.contentContainer)}
      />
    ) : null;
  }
);

export default HourStateDropdown;
